// Layout for Agentic Workflow Detail: header, analytics tabs, outlet, shared modals
import React, { useState } from 'react';
import { useParams, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Pause, Play, LoaderCircle, Plus, Clock, Moon, Unplug } from 'lucide-react';
import { updateWorkflowStatus } from 'services/api/WorkflowServiceAPI';
import NotificationModal from 'components/NotificationModal';
import { Notification } from '@slai/ui';
import { Button, Tab, Tooltip, Chip } from 'common/components';
import { ConcurrentCalls, UploadContactsModal } from './components';
import { useGetAgenticWorkflowDetails, useGetEditAgenticWorkflow } from 'services/hooks/queries';
import { setDateTimes } from './utils';
import type { NotificationModalState } from './AgenticWorkflowDetail.types';
import { WorkflowData, WorkflowConfig } from 'pages/WorkflowBuilder/WorkflowBuilder.types';
import { isOutsideOperatingHours } from 'utils/operatingHours';
import styles from './AgenticWorkflowDetail.module.scss';

const defaultDateRange: [Date, Date] = [new Date(), new Date()];
export const { startWithTime: initialStartDate, endWithTime: initialEndDate } = setDateTimes(defaultDateRange);

export const AgenticWorkflowDetailLayout: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const initialWorkflow = (location.state as { workflow?: WorkflowData } | null)?.workflow;

    const [isWorkflowTriggered, setIsWorkflowTriggered] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [notificationModal, setNotificationModal] = useState<NotificationModalState>({
        isOpen: false,
        title: '',
        message: '',
        type: 'success',
        body: null,
        buttons: [],
    });

    const layoutQueryParams = {
        id: id || '',
        startDate: initialStartDate,
        endDate: initialEndDate,
        callStatus: '',
        partyStage: '',
        callDirection: '',
        search: '',
        perPage: 10,
        currentPage: 1,
    };

    // TODO: remove when overview page is implemented
    const {
        agenticWorkflowDetails: workflowDetails,
        refetchAgenticWorkflowDetails,
        isFetched,
    } = useGetAgenticWorkflowDetails(layoutQueryParams);

    const { editAgenticWorkflow: workflowData } = useGetEditAgenticWorkflow({
        id: id || '',
        isEnabled: !!id && !initialWorkflow?.title,
    });

    const workflowStatus = workflowDetails?.status || 'PAUSED';
    const isWorkflowActive = workflowStatus === 'ACTIVE';
    const workflowTitle = initialWorkflow?.title || workflowData?.title || 'Workflow';
    const hasAgentlessQueue = workflowDetails?.allowCallsWithoutOnlineAgents === true;
    const isStartBlockedByNoAgents =
        !isWorkflowActive && workflowDetails?.hasAssignedAgents === false && !hasAgentlessQueue;
    const isAgentlessWithNoAgents =
        !isWorkflowActive && workflowDetails?.hasAssignedAgents === false && hasAgentlessQueue;
    const startButtonTooltip = isStartBlockedByNoAgents
        ? 'No agents assigned to workflow. Make sure you assign them to workflow in "Manager Command Center" > Workflow Assignments.'
        : isAgentlessWithNoAgents
          ? 'This workflow is configured to run without online agents.'
          : '';

    const operationalConfig = workflowData?.config as unknown as WorkflowConfig | undefined;
    const initialConfig = initialWorkflow?.config as unknown as WorkflowConfig | undefined;
    const operatingHours =
        initialWorkflow?.operatingHours || initialConfig?.operatingHours || operationalConfig?.operatingHours;
    const outside = isOutsideOperatingHours(operatingHours ? { operatingHours } : undefined);

    const handleAddContacts = (): void => setIsUploadModalOpen(true);

    const closeUploadModal = (): void => {
        setIsUploadModalOpen(false);
        closeNotificationModal();
    };

    const handleUploadNotification = (notification: NotificationModalState): void => {
        setNotificationModal(notification);
    };

    const closeNotificationModal = (): void => {
        setNotificationModal((prev) => ({ ...prev, isOpen: false }));
    };

    const handleStartCampaign = async (): Promise<void> => {
        if (!id) return;
        setIsWorkflowTriggered(true);
        try {
            await updateWorkflowStatus(id);
            const response = await refetchAgenticWorkflowDetails();
            const newStatus = response?.data?.status || '';
            const isNowActive = newStatus === 'ACTIVE';
            setNotificationModal({
                isOpen: true,
                title: isNowActive ? 'Campaign Started' : 'Campaign Paused',
                message: isNowActive
                    ? 'The campaign has been successfully started.'
                    : 'The campaign has been successfully paused.',
                type: 'success',
                buttons: [],
            });
        } catch (err) {
            console.error('Error updating workflow status:', err);
            setNotificationModal({
                isOpen: true,
                title: 'Status Update Failed',
                message: 'Failed to update campaign status. Please try again.',
                type: 'error',
                buttons: [],
            });
        } finally {
            setIsWorkflowTriggered(false);
        }
    };

    const basePath = `/agentic-workflow/${id}`;
    const pathname = location.pathname;
    const isSmsAnalytics = pathname.endsWith('/analytics/sms');
    const activeTabId = isSmsAnalytics ? 'sms' : 'calls';

    const analyticsTabs = [
        { id: 'calls', label: 'Call Analytics' },
        { id: 'sms', label: 'SMS Analytics' },
    ];

    const handleTabChange = (tabId: string) => {
        navigate(tabId === 'sms' ? `${basePath}/analytics/sms` : basePath);
    };

    if (!id) {
        return <div className={`${styles.container} ${styles.error}`}>Workflow not found.</div>;
    }

    return (
        <div className={styles.container}>
            <div className={styles.workflowHeader}>
                <div>
                    <div className='flex items-center gap-sm'>
                        <h3 className={styles.workflowTitle}>{workflowTitle}</h3>
                        {hasAgentlessQueue && (
                            <Chip size='xs' color='success' variant='outlined' corner='pill' icon={Unplug}>
                                Agent-less
                            </Chip>
                        )}
                    </div>
                    {operatingHours && (
                        <div
                            className='flex items-center gap-xs'
                            style={{ marginTop: '4px', fontSize: '0.85rem', color: 'var(--gray-900)' }}
                        >
                            {outside ? (
                                <Moon size={14} color='var(--gray-700)' />
                            ) : (
                                <Clock size={14} color='var(--success-color)' />
                            )}
                            <span>
                                {operatingHours.startTime === '00:00' && operatingHours.endTime === '23:59'
                                    ? '24 Hours'
                                    : `${operatingHours.startTime} – ${operatingHours.endTime} ${operatingHours.timezone.replace('America/', '').replace('_', ' ')}`}
                            </span>
                            {outside && (
                                <span style={{ color: 'var(--gray-700)', fontStyle: 'italic' }}>(outside hours)</span>
                            )}
                        </div>
                    )}
                </div>
                <div className={styles.workflowActions}>
                    <ConcurrentCalls
                        workflowId={id}
                        workflowDetails={workflowDetails}
                        isLoadingAgenticWorkflowDetails={!isFetched || isWorkflowTriggered}
                    />
                    <Button variant='outline-primary' icon={Plus} onClick={handleAddContacts}>
                        Add Contacts
                    </Button>
                    <Tooltip
                        content={startButtonTooltip}
                        position='bottom'
                        disabled={!isStartBlockedByNoAgents && !isAgentlessWithNoAgents}
                    >
                        <span style={{ display: 'inline-flex' }}>
                            <Button
                                style={{
                                    backgroundColor: isWorkflowActive ? '#f59e0b' : '#9b51e0',
                                    borderColor: isWorkflowActive ? '#f59e0b' : '#9b51e0',
                                }}
                                onClick={handleStartCampaign}
                                icon={isWorkflowTriggered ? LoaderCircle : isWorkflowActive ? Pause : Play}
                                disabled={isWorkflowTriggered || isStartBlockedByNoAgents}
                                isIconSpinning={isWorkflowTriggered}
                            >
                                {isWorkflowActive ? 'Pause Campaign' : 'Start Campaign'}
                            </Button>
                        </span>
                    </Tooltip>
                </div>
            </div>

            {(!isWorkflowActive || Number(workflowDetails?.callsToAgentRatio) < 5) && (
                <div className='px-2xl pb-2xl'>
                    <Notification severity='info' color='info'>
                        {!isWorkflowActive && <div>Start campaign to initiate AI calls.</div>}
                        {Number(workflowDetails?.callsToAgentRatio) < 5 && (
                            <div>Increasing calls per agent may improve campaign performance</div>
                        )}
                    </Notification>
                </div>
            )}

            <div className='p-2xl flex flex-col gap-2xl main-container h-full'>
                <Tab
                    tabs={analyticsTabs}
                    activeTab={activeTabId}
                    onTabChange={handleTabChange}
                    className={styles.analyticsTabs}
                    aria-label='Analytics tabs'
                />

                <div className={`flex flex-col gap-2xl`}>
                    <Outlet
                        context={{
                            refetchWorkflowDetails: refetchAgenticWorkflowDetails,
                            openNotification: setNotificationModal,
                            closeNotification: closeNotificationModal,
                            initialStartDate,
                            initialEndDate,
                        }}
                    />
                </div>
            </div>

            <UploadContactsModal
                isOpen={isUploadModalOpen}
                onClose={closeUploadModal}
                workflowTitle={workflowTitle}
                workflowId={id}
                onUploadSuccess={refetchAgenticWorkflowDetails}
                onNotification={handleUploadNotification}
            />

            <NotificationModal
                isOpen={notificationModal.isOpen}
                onClose={closeNotificationModal}
                title={notificationModal.title}
                message={notificationModal.message}
                type={notificationModal.type}
                body={notificationModal.body}
                buttons={
                    notificationModal.buttons?.length
                        ? notificationModal.buttons
                        : [{ label: 'OK', onClick: closeNotificationModal }]
                }
            />
        </div>
    );
};

export default AgenticWorkflowDetailLayout;
